import { cn } from "@/shared/lib/utils";
import {
  Pagination as PaginationComponent,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import { ItensPerPage } from "./ItensPerPage";
import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";

interface StatsPaginationProps {
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  showPageSizeOptions?: boolean;
  pageSizeOptions?: number[];
  totalItems?: number;
  showTotalItems?: boolean;
  syncWithQueryParams?: boolean;
}

function getPaginationRange(current: number, total: number, delta = 2) {
  const range = [];
  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);

  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  if (left > 2) range.unshift("left-ellipsis");
  if (right < total - 1) range.push("right-ellipsis");

  range.unshift(1);
  if (total > 1) range.push(total);

  return range;
}
export function Pagination({
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  onPageSizeChange,
  showPageSizeOptions,
  pageSizeOptions,
  totalItems,
  showTotalItems,
  syncWithQueryParams = true,
}: StatsPaginationProps) {
  const paginationRange = getPaginationRange(currentPage, totalPages);

  const [searchParams, setSearchParams] = useSearchParams();

  // Inicializa localPage a partir da URL ou currentPage da API
  const initialPage = (() => {
    if (!syncWithQueryParams) return currentPage;
    const urlPage = searchParams.get("page");
    const parsed = Number(urlPage);
    if (parsed && parsed > 0 && parsed <= totalPages) return parsed;
    return currentPage;
  })();

  const [localPage, setLocalPage] = useState(initialPage || 1);

  // Atualiza localPage quando currentPage da API mudar (ex: ao buscar novos dados)
  useEffect(() => {
    setLocalPage(currentPage);
  }, [currentPage]);

  // Atualiza URL quando localPage mudar
  useEffect(() => {
    if (!syncWithQueryParams) return;
    searchParams.set("page", String(localPage));
    if (pageSize) searchParams.set("pageSize", String(pageSize));
    setSearchParams(searchParams, { replace: true });
  }, [localPage, pageSize, searchParams, setSearchParams, syncWithQueryParams]);

  const handleClick = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setLocalPage(page);
    setCurrentPage(page);
  };

  return (
    <div className="mt-4 flex flex-1 flex-col sm:flex-row items-center justify-between gap-4">
      <ItensPerPage
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        showPageSizeOptions={showPageSizeOptions}
        pageSizeOptions={pageSizeOptions}
        totalItems={totalItems}
        currentPage={localPage}
        showTotalItems={showTotalItems}
      />

      <PaginationComponent>
        <PaginationContent className="w-full justify-end">
          <PaginationItem>
            <PaginationPrevious
              onClick={() => handleClick(localPage - 1)}
              className={cn(
                "cursor-pointer text-white/60 hover:text-gray-500",
                localPage === 1 && "opacity-40 cursor-not-allowed pointer-events-none"
              )}
            />
          </PaginationItem>

          {paginationRange.map((page, index) => {
            if (page === "left-ellipsis" || page === "right-ellipsis") {
              return (
                <PaginationItem key={`${page}-${index}`}>
                  <span className="px-2 text-white/60 select-none">...</span>
                </PaginationItem>
              );
            }

            return (
              <PaginationItem key={page}>
                <PaginationLink
                  onClick={() => handleClick(page as number)}
                  className={cn(
                    "cursor-pointer text-white/60 hover:text-gray-500",
                    localPage === page && "text-white bg-white/10"
                  )}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            );
          })}

          <PaginationItem>
            <PaginationNext
              onClick={() => handleClick(localPage + 1)}
              className={cn(
                "cursor-pointer text-white/60 hover:text-gray-500",
                localPage === totalPages && "opacity-40 cursor-not-allowed pointer-events-none"
              )}
            />
          </PaginationItem>
        </PaginationContent>
      </PaginationComponent>
    </div>
  );
}

